import { css, cx } from '@linaria/core'
import type { ComponentProps, FC, ReactNode } from 'react'
import { HoverTooltip } from '#components/HoverTooltip'
import { fragments, style } from '#styles'

const ButtonClass = css`
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;

  /* todo: check if there use cases for XS, S, XL and XXL. If yes add to textstyles and replace font size with text style */

  &:not(.custom) {
    /* size l as default */
    ${fragments.textStyle.button.l}
    padding: 10px 14px;
    
    &.size-xs {
      padding: 4px 5px;
      font-size: 12px;
    } 
  
    &.size-s {
      padding: 7px 8px;
      /* todo: check if this is needed. If yes add to textstyles and replace font size with text style */
      font-size: 14px;
    }
  
    &.size-m {
      padding: 10px 14px;
      ${fragments.textStyle.button.m}/* line-height: 90%; */
    }
  
    &.size-l {
      padding: 12px 16px;
      ${fragments.textStyle.button.l}/* font-size: 18px; */
    }

  }

  &:disabled {
    cursor: default;
    color: ${style.themeColors.textDisabled};
    background: ${style.themeColors.backgroundDefault};
    transition: background-color 0.125s ease;

    &:hover {
      color: ${style.themeColors.textDisabled};
      background: ${style.themeColors.backgroundDefault};
    }
  }
  
  @layer components {
    &.custom {
      background: transparent;
    }
  }

  &.accent,
  &.primary {
    &:disabled {
      border: none;
      color: ${style.themeColors.textDisabled};
      background: ${style.themeColors.backgroundBoxedHover};
      transition: background-color 0.125s ease;
      cursor: default;

      &:hover {
        border: none;
        color: ${style.themeColors.textDisabled};
        background: ${style.themeColors.backgroundBoxedHover};
      }
    }
  }

  &.accent {
    border: none;
    color: ${style.themeColors.textPrimary};
    background: ${style.themeColors.backgroundAccent};

    &:hover {
      background: ${style.themeColors.backgroundAccentHover};
      transition: background-color 0.125s ease;
    }
  }

  &.primary {
    border: none;
    color: ${style.themeColors.textPrimary};
    background: ${style.themeColors.backgroundPrimary};

    &:hover {
      color: ${style.themeColors.textPrimary};
      background: ${style.themeColors.backgroundPrimaryHover};
      transition: background-color 0.125s ease;
    }
  }

  &.secondary {
    color: ${style.themeColors.textDefault};
    outline: 2px solid ${style.themeColors.textDefault};
    outline-offset: -2px;
    background: ${style.themeColors.backgroundDefault};


    &:disabled, &:disabled:hover {
      outline-color: ${style.themeColors.backgroundBoxedHover};
      background: ${style.themeColors.backgroundBoxed};
      color: ${style.themeColors.textDisabled};
    }

    &:hover {
      background: ${style.themeColors.backgroundSecondaryHover};
    }
  }

  &.plain {
    color: ${style.themeColors.textDefault};
    background: transparent;
    outline: 1px solid transparent;
    outline-offset: -1px;

    &:hover {
      background: ${style.themeColors.backgroundBoxedHover};
    }

    &:disabled, &:disabled:hover {
      color: ${style.themeColors.textDisabled};
      background: ${style.themeColors.backgroundDefault};
      border: none;
    }
  }

  &.plain-text {
    color: ${style.themeColors.textDefault};
    background: transparent;
    padding: 0;

    &:hover {
      color: ${style.themeColors.textImportant};
    }

    &:disabled, &:disabled:hover {
      color: ${style.themeColors.textDisabled};
      border: none;
    }
  }
  
  &.plain-accent {
    color: ${style.themeColors.textImportant};
    background: transparent;
    // border: 1px solid transparent;

    &:hover {
      background: transparent;
      color: ${style.themeColors.textDefault};
    }
    
    &:disabled, &:disabled:hover {
      color: ${style.themeColors.textDisabled};
    }
  }

  &.tab {
    padding: 8px 0;
    height: 100%;
    display: flex;
    align-items: center;
    color: ${style.themeColors.textDefault};
    border: none;
    background: transparent;
    border-bottom: 2px solid ${style.themeColors.lineDefault};
    transition: ease 0.25s border-color;
  
    ${fragments.textStyle.tab.s};
    overflow: hidden;
    
    &.size-m {
      padding: 8px 0;
    }
  
    &.active {
      border-bottom: 2px solid ${style.themeColors.lineImportant};
      color: ${style.themeColors.textImportant};
    } 
  }
  
  &.flex {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

export type ButtonProps = {
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  kind?: 'accent' | 'secondary' | 'plain' | 'plain-text' | 'tab' | 'primary' | 'plain-accent' | 'custom'
  className?: string
  title?: ReactNode
  children?: ReactNode
  forceTooltipOpen?: boolean
  allowTooltipWhenDisabled?: boolean
} & Omit<ComponentProps<'button'>, 'title'>

export const Button: FC<ButtonProps> = ({ ref, size, className, kind, children, forceTooltipOpen, allowTooltipWhenDisabled, title, ...props }) => {
  size ??= 'm'
  kind ??= 'accent'

  if (!title) {
    return (
      <button className={cx(ButtonClass, `size-${size}`, kind, className)} type="button" {...props} ref={ref}>
        {children}
      </button>
    )
  }

  return (
    <HoverTooltip.button
      className={cx(ButtonClass, `size-${size}`, kind, className)}
      type="button"
      {...props}
      title={title}
      forceOpen={forceTooltipOpen}
      allowTooltipWhenDisabled={allowTooltipWhenDisabled}
      ref={ref}
    >
      {children}
    </HoverTooltip.button>
  )
}
